import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  cancelAccountingEntry,
  createAccountingEntry,
  getAccountingEntries,
  getAccountingEntryById,
  getAccountingFormData,
} from "../../services/accountingService"



const formatMoney = (value, currency = "USD") =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))


const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("fr-FR").format(date)
}


const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (detail && typeof detail === "object") {
    return detail.message || detail.detail || fallback
  }

  return error?.message || fallback
}



const emptyLine = {
  account_id: "",
  description: "",
  debit: "",
  credit: "",
}


const makeEmptyForm = () => ({
  journal_id: "",
  entry_date: new Date().toISOString().slice(0, 10),
  reference: "",
  description: "",
  currency: "USD",
  exchange_rate: 1,
  lines: [
    { ...emptyLine },
    { ...emptyLine },
  ],
})


export default function JournalEntriesPage() {
  const [rows, setRows] = useState([])
  const [formData, setFormData] = useState({ accounts: [], journals: [] })
  const [search, setSearch] = useState("")
  const [journalId, setJournalId] = useState("all")
  const [status, setStatus] = useState("all")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [form, setForm] = useState(makeEmptyForm())

  const loadData = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const [entriesResult, formResult] = await Promise.all([
        getAccountingEntries({
          search,
          journal_id: journalId,
          status,
        }),
        getAccountingFormData(),
      ])

      setRows(entriesResult?.items || [])
      setFormData(formResult || { accounts: [], journals: [] })
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          "Impossible de charger les écritures."
        )
      )
    } finally {
      setLoading(false)
    }
  }, [search, journalId, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const updateLine = (index, field, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) =>
        lineIndex === index
          ? { ...line, [field]: value }
          : line
      ),
    }))
  }

  const addLine = () => {
    setForm((current) => ({
      ...current,
      lines: [...current.lines, { ...emptyLine }],
    }))
  }

  const removeLine = (index) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.filter((_, lineIndex) => lineIndex !== index),
    }))
  }

  const save = async () => {
    try {
      await createAccountingEntry({
        journal_id: Number(form.journal_id),
        entry_date: form.entry_date,
        reference: form.reference || null,
        description: form.description,
        currency: form.currency || "USD",
        exchange_rate: Number(form.exchange_rate || 1),
        source_type: "MANUAL",
        source_id: null,
        lines: form.lines.map((line) => ({
          account_id: Number(line.account_id),
          description: line.description || null,
          debit: Number(line.debit || 0),
          credit: Number(line.credit || 0),
        })),
      })

      setDialogOpen(false)
      setForm(makeEmptyForm())
      await loadData()
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          "Impossible d'enregistrer l'écriture."
        )
      )
    }
  }

  const openDetail = async (row) => {
    try {
      const result = await getAccountingEntryById(row.id)
      setDetail(result)
      setDetailOpen(true)
    } catch (err) {
      setError(extractErrorMessage(err, "Impossible de charger le détail."))
    }
  }

  const cancelEntry = async (row) => {
    if (!window.confirm("Annuler cette écriture comptable ?")) return

    try {
      await cancelAccountingEntry(row.id, {
        notes: "Annulation depuis le registre comptable",
      })
      await loadData()
    } catch (err) {
      setError(extractErrorMessage(err, "Impossible d'annuler l'écriture."))
    }
  }

  const columns = useMemo(
    () => [
      {
        field: "entry_date",
        headerName: "Date",
        minWidth: 120,
        valueFormatter: (value) => formatDate(value),
      },
      { field: "entry_code", headerName: "N° écriture", minWidth: 150 },
      { field: "journal_code", headerName: "Journal", minWidth: 100 },
      { field: "description", headerName: "Description", minWidth: 240, flex: 1 },
      {
        field: "source_type",
        headerName: "Source",
        minWidth: 150,
        valueGetter: (_, row) => row.source_type || "MANUAL",
      },
      {
        field: "total_debit",
        headerName: "Débit",
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "total_credit",
        headerName: "Crédit",
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
      { field: "status", headerName: "Statut", minWidth: 110 },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 180,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={() => openDetail(params.row)}>
              Voir
            </Button>

            {params.row.status === "POSTED" && (
              <Button
                size="small"
                color="error"
                onClick={() => cancelEntry(params.row)}
              >
                Annuler
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    []
  )

  const totalDebit = form.lines.reduce(
    (sum, line) => sum + Number(line.debit || 0),
    0
  )

  const totalCredit = form.lines.reduce(
    (sum, line) => sum + Number(line.credit || 0),
    0
  )

  return (
    <MainLayout>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Écritures comptables
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Journal central des écritures automatiques et manuelles.
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={() => {
              setForm(makeEmptyForm())
              setDialogOpen(true)
            }}
          >
            Nouvelle écriture
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" },
              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Rechercher"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <FormControl size="small">
              <InputLabel>Journal</InputLabel>
              <Select
                label="Journal"
                value={journalId}
                onChange={(event) => setJournalId(event.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                {formData.journals?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {`${item.code} — ${item.name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                label="Statut"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="POSTED">Comptabilisée</MenuItem>
                <MenuItem value="CANCELLED">Annulée</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: 600 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              sx={{ border: 0 }}
            />
          </Box>
        </Paper>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>Nouvelle écriture comptable</DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <FormControl>
                  <InputLabel>Journal *</InputLabel>
                  <Select
                    label="Journal *"
                    value={form.journal_id}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        journal_id: event.target.value,
                      }))
                    }
                  >
                    {formData.journals?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {`${item.code} — ${item.name}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Date *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.entry_date}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      entry_date: event.target.value,
                    }))
                  }
                />

                <TextField
                  label="Référence"
                  value={form.reference}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      reference: event.target.value,
                    }))
                  }
                />
              </Box>

              <TextField
                label="Description *"
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />

              <Typography variant="h6" fontWeight={800}>
                Lignes
              </Typography>

              {form.lines.map((line, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "2fr 2fr 1fr 1fr auto",
                      },
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <FormControl size="small">
                      <InputLabel>Compte</InputLabel>
                      <Select
                        label="Compte"
                        value={line.account_id}
                        onChange={(event) =>
                          updateLine(
                            index,
                            "account_id",
                            event.target.value
                          )
                        }
                      >
                        {formData.accounts?.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {`${account.code} — ${account.name}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Description"
                      value={line.description}
                      onChange={(event) =>
                        updateLine(
                          index,
                          "description",
                          event.target.value
                        )
                      }
                    />

                    <TextField
                      size="small"
                      label="Débit"
                      type="number"
                      value={line.debit}
                      onChange={(event) =>
                        updateLine(
                          index,
                          "debit",
                          event.target.value
                        )
                      }
                    />

                    <TextField
                      size="small"
                      label="Crédit"
                      type="number"
                      value={line.credit}
                      onChange={(event) =>
                        updateLine(
                          index,
                          "credit",
                          event.target.value
                        )
                      }
                    />

                    <Button
                      color="error"
                      disabled={form.lines.length <= 2}
                      onClick={() => removeLine(index)}
                    >
                      Supprimer
                    </Button>
                  </Box>
                </Paper>
              ))}

              <Button variant="outlined" onClick={addLine}>
                Ajouter une ligne
              </Button>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end">
                <Typography fontWeight={800}>
                  Débit : {formatMoney(totalDebit)}
                </Typography>

                <Typography fontWeight={800}>
                  Crédit : {formatMoney(totalCredit)}
                </Typography>
              </Stack>

              {Math.abs(totalDebit - totalCredit) > 0.005 && (
                <Alert severity="warning">
                  L'écriture n'est pas équilibrée.
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>

            <Button
              variant="contained"
              disabled={Math.abs(totalDebit - totalCredit) > 0.005}
              onClick={save}
            >
              Comptabiliser
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Détail de l'écriture</DialogTitle>

          <DialogContent dividers>
            {detail && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    {detail.entry_code}
                  </Typography>

                  <Typography color="text.secondary">
                    {formatDate(detail.entry_date)} • {detail.journal_code}
                  </Typography>

                  <Typography sx={{ mt: 1 }}>
                    {detail.description}
                  </Typography>
                </Box>

                {detail.lines?.map((line) => (
                  <Paper
                    key={line.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box>
                        <Typography fontWeight={800}>
                          {`${line.account_code} — ${line.account_name}`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {line.description || "-"}
                        </Typography>
                      </Box>

                      <Typography fontWeight={800}>
                        {line.debit > 0
                          ? `Débit ${formatMoney(line.debit)}`
                          : `Crédit ${formatMoney(line.credit)}`
                        }
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </MainLayout>
  )
}
